import type { MouseEvent } from "react";
import { useCallback, useEffect } from "react";
import { useDownload } from "../../hooks/useDownload";
import { useToast } from "../../mantine/notifications/useToast";
import { AnchorLink } from "../AnchorLink/AnchorLink";

interface MarkdownLinkProps {
  children?: React.ReactNode;
  href?: string;
}

function isInternalApiLink(href?: string): href is string {
  return href?.startsWith("/api/") ?? false;
}

/**
 * Relative `/api/` links (e.g. document downloads referenced as markdown links
 * in regulatory-duty descriptions) cannot be opened via a plain browser
 * navigation: the JWT lives in memory and is only attached by the axios
 * interceptor, so a top-level navigation reaches the API without an
 * Authorization header and gets a 401. For these we intercept the click and
 * fetch the file through the authenticated api client instead. All other
 * links keep their normal behavior.
 */
function InternalApiLink({
  children,
  href
}: MarkdownLinkProps & { href: string }) {
  const { download, error } = useDownload();
  const { showErrorToast } = useToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error, showErrorToast]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      void download({ downloadUrl: href });
    },
    [href, download]
  );

  return (
    <AnchorLink
      forceVariant="external"
      href="#"
      target="_self"
      onClick={handleClick}
    >
      {children}
    </AnchorLink>
  );
}

function NavigationLink({ children, href }: MarkdownLinkProps) {
  // react-markdown types `href` as optional; without a destination this is just text.
  if (!href) {
    return <>{children}</>;
  }

  return <AnchorLink href={href}>{children}</AnchorLink>;
}

function MarkdownLink({ children, href }: MarkdownLinkProps) {
  if (isInternalApiLink(href)) {
    return <InternalApiLink href={href}>{children}</InternalApiLink>;
  }

  return <NavigationLink href={href}>{children}</NavigationLink>;
}

export { MarkdownLink };
